import { useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useOutletContext } from 'react-router-dom'

import { Box, Spinner } from '@factorialco/deprecated-design-system'
import { PerformanceAnswersDocument } from 'generated/resources/graphql'

import { useQuery } from 'modules/core/hooks/useQuery'

import { QuantitativeHeatmapTab } from 'modules/performance_process/components/insights/Answers/QuantitativeHeatmapTab'
import {
  Filters,
  useFilterManager,
} from 'modules/performance_process/components/review/show/Filters'
import { useFiltersValues } from 'modules/performance_process/components/review/show/Filters/context'
import { ReviewOverviewOutletContext } from 'modules/performance_process/pages/reviews/show/overview'

const ReviewOverviewHeatmapPage = () => {
  const {
    reviewProcess,
    canOnlySeePersonalResults,
    materializedTargetIds,
    teams,
    locations,
    legalEntities,
  } = useOutletContext<ReviewOverviewOutletContext>()
  const filters = useFiltersValues()

  const ref = useRef<HTMLDivElement>(null)
  const [height, setHeight] = useState(0)

  const { data, loading } = useQuery(PerformanceAnswersDocument, {
    variables: {
      reviewId: reviewProcess.id,
      managedByFilter: filters.managerId
        ? {
            managerEmployeeId: filters.managerId,
            onlyDirectReports: filters.onlyDirectReports ?? true,
          }
        : undefined,
      employeeIds: filters.employeeIds,
      teamIds: filters.teamIds,
      tenureDates: filters.tenureDates,
      workplaceIds: filters.workplaceIds,
      legalEntityIds: filters.legalEntityIds,
    },
  })

  const reviewQuestionnaireByReviewerStrategy =
    data?.performance?.reviewProcess?.questionnaireByReviewerStrategies

  useLayoutEffect(() => {
    if (ref.current) setHeight(ref.current.clientHeight)
  }, [ref, setHeight])

  const filterProps = useMemo(
    () => ({
      materializedTargetIds,
      teams,
      locations,
      legalEntities,
    }),
    [materializedTargetIds, teams, locations, legalEntities]
  )

  const { persistentFilters, conditionalFilters, availableFilters, onSelectFilter } =
    useFilterManager({
      configType: 'heatmap',
      filterProps,
    })

  if (canOnlySeePersonalResults) return null

  return (
    <Box flexGrow gap="s16">
      <Filters.FilterManager
        persistentFilters={persistentFilters}
        conditionalFilters={conditionalFilters}
        availableFilters={availableFilters}
        onSelectFilter={onSelectFilter}
      />

      <Box flexGrow ref={ref} style={{ height: `${height}px` }}>
        {loading && (
          <Box flexGrow alignItems="center" justifyContent="center">
            <Spinner />
          </Box>
        )}
        {data && reviewQuestionnaireByReviewerStrategy && filters.reviewerStrategy && (
          <Box flexGrow overflowY="auto" height="full">
            <QuantitativeHeatmapTab
              answersQuery={data}
              reviewQuestionnaireByReviewerStrategy={reviewQuestionnaireByReviewerStrategy}
              reviewType={filters.reviewerStrategy}
            />
          </Box>
        )}
      </Box>
    </Box>
  )
}

export default ReviewOverviewHeatmapPage
